/*
 * <sticky-atc>
 * A sticky add-to-cart bar that reveals once the shopper scrolls past the main
 * product buy buttons. Carries its own <product-form> + variant <select> so it can
 * add to cart independently, while staying in two-way sync with the main product
 * variant picker (so price, media and URL on the rest of the PDP keep up).
 */
if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      constructor() {
        super();
        this.variants = this.parseVariants();
      }

      connectedCallback() {
        // position: fixed breaks inside ancestors that establish a containing block
        // (the sticky info column / scroll-reveal animations both apply transforms).
        // Portal the bar to <body> so it always pins to the viewport. Moving the node
        // re-triggers connectedCallback, so init runs on the second pass (now under body).
        if (this.parentElement !== document.body) {
          document
            .querySelectorAll(`body > sticky-atc[data-section-id="${this.dataset.sectionId}"]`)
            .forEach((el) => {
              if (el !== this) el.remove();
            });
          document.body.appendChild(this);
          return;
        }

        this.sectionId = this.dataset.sectionId;
        this.select = this.querySelector('[data-sticky-variant-select]');
        this.submit = this.querySelector('[data-sticky-submit]');
        this.submitText = this.submit ? this.submit.querySelector('span') : null;
        this.bisTrigger = this.querySelector('[data-sticky-bis]');
        if (this.bisTrigger) {
          this.bisTrigger.addEventListener('click', (e) => {
            e.preventDefault();
            const kl = this.getBisButton();
            if (kl) kl.click();
            else this.updateBisTrigger();
          });
        }
        this.priceContainer = this.querySelector('[data-sticky-price]');

        if (this.select) {
          this.select.addEventListener('change', this.onSelectChange.bind(this));
        }

        if (typeof subscribe === 'function') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const data = event.data || {};
            if (data.sectionId && data.sectionId !== this.sectionId) return;
            this.updateForVariant(data.variant?.id);
          });
        }

        this.initVisibility();
        this.renderVariant(this.variants[this.select?.value || this.querySelector('input[name="id"]')?.value]);
        this.initDesignMode();
        this.pinChatWidget();
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
        if (this.observer) this.observer.disconnect();
        if (this.mobileQuery) this.mobileQuery.removeEventListener('change', this.onBreakpointChange);
        if (this.onVisibilityChange) {
          window.removeEventListener('scroll', this.onVisibilityChange);
          window.removeEventListener('resize', this.onVisibilityChange);
          window.removeEventListener('pageshow', this.onVisibilityChange);
        }
        cancelAnimationFrame(this.visibilityFrame);
        this.visibilityFrame = null;
        if (this.chatObserver) this.chatObserver.disconnect();
        clearInterval(this.chatPoll);
        clearTimeout(this.suppressScrollTimer);
      }

      // Belt-and-suspenders for the CSS chat override: the Shopify Inbox widget writes
      // its near-max z-index inline at runtime. A stylesheet !important normally beats
      // that (its inline value isn't !important), but to be robust against future builds
      // we also pin the z-index INLINE-important here (which beats any inline value),
      // re-asserting when the widget re-applies styles on open/close — mirroring utils.js.
      pinChatWidget() {
        const CHAT_Z = '990'; // below the bar (998), above page content
        const pin = () => {
          const els = document.querySelectorAll(
            '#shopify-chat, #shopify-chat inbox-online-store-chat, iframe#ShopifyChat, iframe#dummy-chat-button-iframe'
          );
          if (!els.length) return false;
          els.forEach((el) => el.style.setProperty('z-index', CHAT_Z, 'important'));
          const host = document.querySelector('#shopify-chat inbox-online-store-chat');
          if (host && !this.chatObserver) {
            this.chatObserver = new MutationObserver(() => {
              els.forEach((el) => el.style.setProperty('z-index', CHAT_Z, 'important'));
            });
            this.chatObserver.observe(host, { attributes: true, attributeFilter: ['is-open'] });
          }
          return true;
        };

        // The widget loads async — poll briefly until it exists, then stop.
        if (pin()) return;
        let tries = 0;
        this.chatPoll = setInterval(() => {
          if (pin() || ++tries > 40) clearInterval(this.chatPoll);
        }, 300);
      }

      parseVariants() {
        const json = this.querySelector('[data-sticky-variants]');
        if (!json) return {};
        try {
          return JSON.parse(json.textContent).reduce((map, variant) => {
            map[variant.id] = variant;
            return map;
          }, {});
        } catch {
          return {};
        }
      }

      onSelectChange() {
        const variant = this.variants[this.select.value];
        this.renderVariant(variant);
        this.syncToMainPicker(variant);
      }

      // Reflect a variant chosen elsewhere (the main picker) onto the sticky bar.
      updateForVariant(id) {
        if (this.select && this.select.value !== String(id)) {
          this.select.value = String(id);
        }
        this.renderVariant(this.variants[id]);
      }

      renderVariant(variant) {
        this.currentVariant = variant;
        if (!variant) {
          if (this.bisTrigger) this.bisTrigger.hidden = true;
          if (this.submit) this.submit.hidden = false;
          this.toggleSubmit(false, window.variantStrings && window.variantStrings.unavailable);
          return;
        }

        if (this.priceContainer && variant.price) {
          let html = '';
          if (variant.compare_at_price && variant.compare_at_price !== variant.price) {
            html += `<s class="sticky-atc__price-compare">${variant.compare_at_price}</s>`;
          }
          html += `<span class="sticky-atc__price-current">${variant.price}</span>`;
          this.priceContainer.innerHTML = html;
        }

        const strings = window.variantStrings || {};
        if (this.bisTrigger) {
          this.bisTrigger.dataset.variantId = variant.id;
        }
        this.updateBisTrigger();

        this.toggleSubmit(
          variant.available,
          variant.available ? strings.addToCart : variant.eol ? strings.soldOut : this.dataset.comingSoon
        );
      }

      getBisButton() {
        if (!document.documentElement.classList.contains('bis-allowed')) return null;
        const product = document.getElementById(`ProductSubmitButton-${this.sectionId}`)
          ?.closest('product-info[data-update-url="true"]');
        if (!product || product.dataset.bisPending === 'true') return null;
        return product.querySelector(
          '#klaviyo-bis-button-container button:not([disabled]):not([aria-disabled="true"]), ' +
          '[data-a11y-identifier^="bis-button-"] button:not([disabled]):not([aria-disabled="true"]), ' +
          'button[data-a11y-identifier^="bis-button-"]:not([disabled]):not([aria-disabled="true"])'
        );
      }

      updateBisTrigger() {
        const showBis = this.currentVariant?.bis === true && !!this.getBisButton();
        if (this.bisTrigger) this.bisTrigger.hidden = !showBis;
        if (this.submit) this.submit.hidden = showBis && !!this.bisTrigger;
      }

      toggleSubmit(enabled, text) {
        if (!this.submit) return;
        if (enabled) {
          this.submit.removeAttribute('disabled');
        } else {
          this.submit.setAttribute('disabled', 'disabled');
        }
        if (text && this.submitText) this.submitText.textContent = text;
      }

      // Drive the main product variant picker so the rest of the PDP (price, media,
      // URL) updates too. The sticky form submits its own <select name="id"> value,
      // so add-to-cart stays correct even if this sync is a no-op.
      syncToMainPicker(variant) {
        if (!variant || !variant.options) return;

        const main = document.getElementById(`variant-selects-${this.sectionId}`);
        if (!main) return;

        const groups = main.querySelectorAll('.product-form__input');
        let targetInput = null;
        let changed = false;

        variant.options.forEach((value, index) => {
          const group = groups[index];
          if (!group) return;

          const radios = group.querySelectorAll('input[type="radio"]');
          if (radios.length) {
            const match = Array.from(radios).find((radio) => radio.value === value);
            if (match) {
              if (!match.checked) {
                match.checked = true;
                changed = true;
              }
              targetInput = match;
            }
            return;
          }

          const select = group.querySelector('select');
          if (select) {
            if (select.value !== value) changed = true;
            Array.from(select.options).forEach((option) => option.removeAttribute('selected'));
            const match = Array.from(select.options).find((option) => option.value === value);
            if (match) {
              match.setAttribute('selected', 'selected');
              select.value = value;
            }
            targetInput = select;
          }
        });

        // A single change event is enough: VariantSelects reads the full current
        // selection, and product-info aborts/replaces in-flight requests.
        if (changed && targetInput) {
          // Flag the update so the gallery/product-info don't yank the page up to the
          // (off-screen) variant image or picker input. Consumed in media-gallery.js
          // and paired with focus({ preventScroll }) in product-info.js.
          window.stickyAtcSuppressScroll = true;
          targetInput.dispatchEvent(new Event('change', { bubbles: true }));
          // Safety net: if this variant has no featured media the gallery never runs
          // (so never consumes the flag) — clear it so it can't leak into a later change.
          clearTimeout(this.suppressScrollTimer);
          this.suppressScrollTimer = setTimeout(() => {
            window.stickyAtcSuppressScroll = false;
          }, 3000);
        }
      }

      initVisibility() {
        this.trigger =
          document.getElementById(`ProductSubmitButton-${this.sectionId}`)?.closest('.product-form__buttons') ||
          document.getElementById(`ProductSubmitButton-${this.sectionId}`) ||
          document.querySelector(`product-info[data-section="${this.sectionId}"]`) ||
          document.querySelector('product-info');
        if (!this.trigger) return;

        // Opt-in (per block, so only templates that enable it are affected): on mobile,
        // reveal whenever the buy buttons are off screen — including above the fold,
        // before the shopper has scrolled down to them.
        this.showBeforeBuyButtons = this.dataset.showBeforeBuyButtons === 'true';
        this.mobileQuery = window.matchMedia('(max-width: 749px)');
        this.onBreakpointChange = () => this.applyVisibility(this.currentEntry());
        this.mobileQuery.addEventListener('change', this.onBreakpointChange);

        this.onVisibilityChange = () => {
          if (this.visibilityFrame != null) return;
          this.visibilityFrame = requestAnimationFrame(() => {
            this.visibilityFrame = null;
            this.applyVisibility(this.currentEntry());
          });
        };
        window.addEventListener('scroll', this.onVisibilityChange, { passive: true });
        window.addEventListener('resize', this.onVisibilityChange);
        window.addEventListener('pageshow', this.onVisibilityChange);

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => this.applyVisibility(entry));
          },
          { threshold: 0 }
        );
        this.observer.observe(this.trigger);
        this.applyVisibility(this.currentEntry());
      }

      // Same shape as an IntersectionObserverEntry, measured on demand — the observer
      // only fires when the trigger crosses the viewport edge, so breakpoint changes
      // need a fresh reading rather than a stale entry.
      currentEntry() {
        const rect = this.trigger.getBoundingClientRect();
        return {
          isIntersecting: rect.bottom > 0 && rect.top < window.innerHeight,
          boundingClientRect: rect,
        };
      }

      applyVisibility(entry) {
        if (entry.isIntersecting) {
          this.classList.remove('is-visible');
          return;
        }

        // Default: reveal only once the trigger has scrolled up and out of view.
        const scrolledPast = entry.boundingClientRect.top < 0;
        const beforeBuyButtons = this.showBeforeBuyButtons && this.mobileQuery.matches;
        this.classList.toggle('is-visible', scrolledPast || beforeBuyButtons);
      }

      initDesignMode() {
        if (!window.Shopify || !Shopify.designMode) return;

        // Reveal in the theme editor when the merchant selects the block so it can be styled.
        document.addEventListener('shopify:block:select', (event) => {
          if (this === event.target || this.contains(event.target)) this.classList.add('is-visible');
        });
        document.addEventListener('shopify:block:deselect', (event) => {
          if (this === event.target || this.contains(event.target)) this.classList.remove('is-visible');
        });
      }
    }
  );
}
